import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Collapse,
  useTheme,
  useMediaQuery,
  TextField,
} from '@mui/material';
import { Add, Edit, Delete, History, Search, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import UserForm from '../components/users/UserForm';
import { timeAgo } from '../utils/timeAgo';
import { User, UserRole } from '../types/user';
import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} from '../services/userApi';
import { useGetAMCListQuery } from '../services';
import { useNavigate } from 'react-router-dom';

export default function Users() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Get current user's role from Redux store
  const currentUserRole = useSelector((state: any) => state.user.role);
  const isSupport = currentUserRole === UserRole.SUPPORT;
  const currentUserAMC = useSelector((state: any) => state.user.amcIds?.[0]);

  // RTK Query hooks
  const { data: usersData, isLoading, isError, refetch } = useGetUsersQuery();
  const { data: amcs = [] } = useGetAMCListQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const allUsers = usersData?.users || [];

  const filteredUsers = useMemo(() => {
    return allUsers.filter((user) => {
      let matchesSearch = true;
      if (searchQuery) {
        const lowerQuery = searchQuery.toLowerCase();
        matchesSearch =
          (user.name?.toLowerCase() || '').includes(lowerQuery) ||
          (user.email?.toLowerCase() || '').includes(lowerQuery);
      }

      return matchesSearch;
    });
  }, [allUsers, searchQuery]);

  const roleDisplayMap: { [key: string]: string } = {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.SUPPORT]: 'Support',
    [UserRole.AMC]: 'AMC User',
    [UserRole.AMC_ADMIN]: 'AMC Admin',
    [UserRole.AMC_SUPPORT]: 'AMC Support',
  };

  const normalizeRole = (r: any) => {
    if (!r) return '';
    const s = String(r).toLowerCase().trim();
    if (s === 'admin') return UserRole.ADMIN;
    if (s === 'support') return UserRole.SUPPORT;
    if (s === 'amc-user' || s === 'amcuser' || s === 'amc user' || s === 'amc') return UserRole.AMC;
    if (s === 'amc-admin' || s === 'amcadmin' || s === 'amc admin' || s === 'amc_admin') return UserRole.AMC_ADMIN;
    return r;
  };

  const handleEditUser = (user: User) => {
    setCurrentUser(user);
    setOpenForm(true);
  };


  const handleDeleteClick = (userId: string) => {
    setUserToDelete(userId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete).unwrap();
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success',
      });
      refetch();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete user',
        severity: 'error',
      });
    } finally {
      setOpenDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const handleFormSubmit = async (
    userData: Omit<User, 'id'> & { changePassword: boolean }
  ) => {
    if (!currentUser && !userData.password) {
      setSnackbar({
        open: true,
        message: 'Password is required',
        severity: 'error',
      });
      return;
    }

    try {
      if (currentUser) {
        const { password, ...rest } = userData;
        await updateUser({
          id: currentUser.id,
          userData: userData.changePassword ? userData : rest,
        }).unwrap();
        setSnackbar({
          open: true,
          message: 'User updated successfully',
          severity: 'success',
        });
      } else {
        await createUser(userData).unwrap();
        setSnackbar({
          open: true,
          message: 'User created successfully',
          severity: 'success',
        });
      }
      setOpenForm(false);
      refetch();
    } catch (error) {
      console.error('Failed to save user:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${currentUser ? 'update' : 'create'} user`,
        severity: 'error',
      });
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleExpand = (userId: string) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{ mb: isMobile ? 2 : 0, alignSelf: isMobile ? 'flex-start' : 'center' }}
        >
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <IconButton
            color="inherit"
            onClick={() => navigate('/users/history')}
            sx={{ border: '1px solid', borderColor: 'divider' }}
            aria-label="User management history"
          >
            <History />
          </IconButton>
          {!isSupport && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => {
                setCurrentUser(null);
                setOpenForm(true);
              }}
              sx={{ width: isMobile ? 'auto' : 'auto' }}
            >
              Add User
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          display: 'flex',
          p: 2,
          mb: 3,
          alignItems: 'center',
        }}
      >
        <TextField
          InputProps={{
            startAdornment: <Search color="action" sx={{ mr: 1 }} />,
          }}
          fullWidth
          label="Search by name or email"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
        />
      </Box>

      <Box sx={{ width: '100%', mb: 2, position: 'relative' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 3, color: 'error.main' }}>Failed to load users. Please try again.</Box>
        ) : isMobile ? (
          <Box>
            {filteredUsers.length === 0 ? (
              <Typography align="center" sx={{ py: 3 }}>
                No users found
              </Typography>
            ) : (
              filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <Paper key={user.id} sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {user.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    <Box>
                      {!isSupport && (
                        <IconButton
                          onClick={() => handleEditUser(user)}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => navigate(`/user/${user.id}/logs`)}
                        color="default"
                        size="small"
                        aria-label="User history"
                      >
                        <History />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(user.id)}
                        color="error"
                        size="small"
                        disabled={user.email === 'admin@kfintech.com'}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Role: {roleDisplayMap[user.role] || user.role}
                    </Typography>
                    <Tooltip title={user.lastLoggedIn ? new Date(user.lastLoggedIn).toLocaleString() : 'Never logged in'} arrow>
                      <Typography variant="body2" color="text.secondary">
                        Last Login: {timeAgo(user.lastLoggedIn)}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: user.isActive ? 'success.main' : 'error.main',
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2">
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      {user.role === UserRole.ADMIN ? (
                         <Chip
                           label="All AMCs"
                           size="small"
                           color="success"
                           variant="outlined"
                           sx={{ borderRadius: '16px', height: '22px', fontSize: '0.7rem', fontWeight: 500 }}
                         />
                      ) : user.amcAccess && user.amcAccess.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            {user.amcAccess.slice(0, 3).map((a, index) => {
                              const amcName = amcs.find((amc: any) => amc.id === a.amcId)?.name || a.amcId;
                              if (index === 2 && user.amcAccess!.length > 3) {
                                return (
                                  <Tooltip 
                                    key="overflow"
                                    title={user.amcAccess!.slice(2).map(overflowA => amcs.find((amc: any) => amc.id === overflowA.amcId)?.name || overflowA.amcId).join(', ')}
                                    arrow
                                  >
                                    <Chip
                                      label={`+${user.amcAccess!.length - 2} More`}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => handleToggleExpand(user.id)}
                                      sx={{ borderRadius: '16px', height: '22px', fontSize: '0.7rem' }}
                                    />
                                  </Tooltip>
                                );
                              }
                              return (
                                <Chip
                                  key={a.amcId}
                                  label={amcName}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleToggleExpand(user.id)}
                                  sx={{ borderRadius: '16px', height: '22px', fontSize: '0.7rem' }}
                                />
                              );
                            })}
                            <IconButton size="small" onClick={() => handleToggleExpand(user.id)} sx={{ ml: 'auto' }}>
                              {expandedUserId === user.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </Box>
                          <Collapse in={expandedUserId === user.id} timeout="auto" unmountOnExit>
                             <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                               <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                                 AMC Access Details ({user.amcAccess.length}):
                               </Typography>
                               {user.amcAccess.map((a, idx) => {
                                 const amcRole = normalizeRole(a.role || (a as any).accessRole || (a as any).amcRole || (a as any).Role);
                                 return (
                                   <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed', borderColor: 'divider' }}>
                                     <Typography variant="caption">{amcs.find((amc: any) => amc.id === a.amcId)?.name || a.amcId}</Typography>
                                     <Chip label={roleDisplayMap[amcRole] || amcRole || 'N/A'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                   </Box>
                                 );
                               })}
                             </Box>
                          </Collapse>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No Access</Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>AMCs</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user) => (
                        <Fragment key={user.id}>
                          <TableRow 
                            sx={{ 
                              '& > *': { borderBottom: 'unset !important' },
                              bgcolor: expandedUserId === user.id ? 'action.hover' : 'inherit',
                            }}
                          >
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{roleDisplayMap[user.role] || user.role}</TableCell>
                            <TableCell>
                              {user.role === UserRole.ADMIN ? (
                                <Chip
                                  label="All AMCs"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ borderRadius: '16px', height: '24px', fontSize: '0.75rem', fontWeight: 500 }}
                                />
                              ) : user.amcAccess && user.amcAccess.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                  {user.amcAccess.slice(0, 2).map((a) => (
                                    <Chip
                                      key={a.amcId}
                                      label={amcs.find((amc: any) => amc.id === a.amcId)?.name || a.amcId}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => handleToggleExpand(user.id)}
                                      sx={{ borderRadius: '16px', height: '24px', fontSize: '0.75rem', cursor: 'pointer' }}
                                    />
                                  ))}
                                  {user.amcAccess.length > 2 && (
                                    <Tooltip 
                                      title={user.amcAccess.slice(2).map(a => amcs.find((amc: any) => amc.id === a.amcId)?.name || a.amcId).join(', ')}
                                      arrow
                                    >
                                      <Chip
                                        label={`+${user.amcAccess.length - 2} More`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => handleToggleExpand(user.id)}
                                        sx={{ borderRadius: '16px', height: '24px', fontSize: '0.75rem', cursor: 'pointer' }}
                                      />
                                    </Tooltip>
                                  )}
                                  <IconButton size="small" onClick={() => handleToggleExpand(user.id)}>
                                    {expandedUserId === user.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No Access
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={user.lastLoggedIn ? new Date(user.lastLoggedIn).toLocaleString() : 'Never logged in'} arrow>
                                <span>{timeAgo(user.lastLoggedIn)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: user.isActive ? 'success.main' : 'error.main',
                                  mr: 1,
                                }}
                              />
                              {user.isActive ? 'Active' : 'Inactive'}
                            </TableCell>
                            <TableCell align="right">
                              {!isSupport && (
                                <IconButton onClick={() => handleEditUser(user)} color="primary">
                                  <Edit />
                                </IconButton>
                              )}
                              <IconButton
                                onClick={() => navigate(`/user/${user.id}/logs`)}
                                color="default"
                                aria-label="User history"
                              >
                                <History />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDeleteClick(user.id)}
                                color="error"
                                disabled={user.email === 'admin@kfintech.com'}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                              <Collapse in={expandedUserId === user.id} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 2, display: 'flex', gap: 4 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                      AMC Access Details
                                      {user.role !== UserRole.ADMIN && (
                                        <Typography variant="caption" sx={{ bgcolor: 'primary.main', color: 'common.white', px: 1, py: 0.2, borderRadius: 1, fontSize: '12px' }}>
                                          {user.amcAccess?.length || 0} Total
                                        </Typography>
                                      )}
                                    </Typography>
                                    {user.role === UserRole.ADMIN ? (
                                      <Box sx={{ p: 2, bgcolor: 'success.light', color: 'success.dark', borderRadius: 2, bgOpacity: 0.1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          Global Administrator Access
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                          This user has unrestricted access to all AMCs and system configurations.
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                        <Table size="small">
                                          <TableHead sx={{ bgcolor: 'action.hover' }}>
                                            <TableRow>
                                              <TableCell sx={{ fontWeight: 'bold' }}>AMC Name</TableCell>
                                              <TableCell sx={{ fontWeight: 'bold' }}>Access Level</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {user.amcAccess?.map((a, idx) => {
                                              const amcRole = normalizeRole(a.role || (a as any).accessRole || (a as any).amcRole || (a as any).Role);
                                              return (
                                                <TableRow key={idx}>
                                                  <TableCell>{amcs.find((amc: any) => amc.id === a.amcId)?.name || a.amcId}</TableCell>
                                                  <TableCell>
                                                    <Chip label={roleDisplayMap[amcRole] || amcRole || 'N/A'} size="small" variant="outlined" />
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Box>

      <UserForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSubmit={handleFormSubmit}
        user={currentUser}
        amcId={currentUserAMC}
      />

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this user? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
